import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuestionSort } from '../questions-hooks.tsx';
import { useQuestionFilter } from './questions-filter/filter-utils.ts';
import { useMutation, useQuery, useSuspenseQuery } from '@apollo/client';
import {
  GET_QUESTIONS,
  QUESTION_TABLE_FRAGMENT,
  REMOVE_QUESTION,
} from '../../../apollo/questions.ts';
import { useFragment } from '../../../apollo/__generated__';
import { uniqBy } from 'lodash';
import QuestionsFilter from './questions-filter.tsx';
import { questionsBrowserFilters } from './questions-filter/filter-constants.ts';
import QuestionsTable from './questions-table.tsx';
import {
  EntityCreatedRole,
  QuestionAnswerType,
  QuestionSourceRelationTypeEnum,
  QuestionStatus,
  QuestionTableFragment,
} from '../../../apollo/__generated__/graphql.ts';
import ActionsDropdown from '../../table/actions-dropdown.tsx';
import {
  useDeleteItems,
  useFetchMore,
  useSelectItem,
} from '../../../utils/hooks.ts';
import QuestionIdTableComponent from './question-id-table-component.tsx';
import Difficulty from '../../table/difficulty.tsx';
import Tag from '../../common/tag.tsx';
import AnswerCell from './answer-cell.tsx';
import Markdown from '../../markdown.tsx';
import { capitalizeWords } from '../../../utils/capitalize-words.ts';
import useToggleContentCreator from '../../common/use-toggle-content-creator.tsx';
import { NavBarEnum } from '../../../utils/enums.ts';
import { GET_QUESTION_SOURCE_RELATION_TYPE } from '../../../apollo/question-source-relation-type.ts';
import Button from '../../common/button.tsx';
import { cn } from 'nalaprep-pdf';
import { questionDourceRelationTypeColorOrder } from '../../../constants/question-source-relation-type-color-order.ts';
import Modal from '../../common/modal.tsx';
import QuestionPreview from '../question-content/question-preview.tsx';
import { Popover } from '../../common/popover.tsx';

interface IQuestionsBrowser {
  isQuestionsDrafts?: boolean;
  isModalMode?: boolean; // modal for add questions in quiz
  setSelectedQuestion?: Dispatch<SetStateAction<number[]>>;
}

type SourceRelationFamilySelection = {
  exclude: boolean;
  isCustom: boolean;
  selectedTypeIds: number[];
};

type SourceRelationSelectionState = Record<
  QuestionSourceRelationTypeEnum,
  SourceRelationFamilySelection
>;

const QuestionsBrowser = ({
  isQuestionsDrafts = false,
  isModalMode = false,
  setSelectedQuestion,
}: IQuestionsBrowser) => {
  const FETCH_LIMIT = 20;
  const { ContentToggle, isShowTeacherContent, isContentToggleAvailable } =
    useToggleContentCreator();

  const [page, setPage] = useState<number>(1);
  const [sort, setQuestionSort] = useQuestionSort();

  const [excludeEmptySourceRelationType, setExcludeEmptySourceRelationType] =
    useState<boolean>(false);

  const allRelationTypes = useMemo(
    () => Object.values(QuestionSourceRelationTypeEnum),
    [],
  );
  const [sourceRelationSelection, setSourceRelationSelection] =
    useState<SourceRelationSelectionState>(() =>
      allRelationTypes.reduce(
        (acc, type) => ({
          ...acc,
          [type]: {
            exclude: false,
            isCustom: false,
            selectedTypeIds: [],
          },
        }),
        {} as SourceRelationSelectionState,
      ),
    );

  const { setIsDeleted, onDelete } = useDeleteItems();
  const [familyToggle, setFamilyToggle] = useState(false);
  const [previewQuestion, setPreviewQuestion] = useState<{
    id: number;
    index: number;
  } | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const resetTable = useCallback(() => setPage(1), []);
  const initialFilterState = useMemo(
    () => ({
      status: isQuestionsDrafts ? [QuestionStatus.Draft] : undefined,
    }),
    [isQuestionsDrafts],
  );

  const {
    filterInput,
    filters,
    setFilters,
    onAddFilter,
    onRemoveFilter,
    onClearFilter,
  } = useQuestionFilter(resetTable, initialFilterState);

  const isFamilyAvailable = useMemo(() => {
    const originalIdFilter = filters.find(
      (filter) => filter.data.value === 'originalId',
    );
    const originalIdValue = originalIdFilter?.inputValue.trim();
    const originalIds = originalIdValue
      ?.split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    return Boolean(originalIds?.length === 1 && /^\d+$/.test(originalIds[0]));
  }, [filters]);

  useEffect(() => {
    if (familyToggle && !isFamilyAvailable) {
      setFamilyToggle(false);
    }
  }, [familyToggle, isFamilyAvailable]);

  const { data: questionSourceRelationTypes } = useSuspenseQuery(
    GET_QUESTION_SOURCE_RELATION_TYPE,
    {
      errorPolicy: 'all',
    },
  );

  const sourceRelationTypes = useMemo(
    () => questionSourceRelationTypes?.questionSourceRelationType.getAll ?? [],
    [questionSourceRelationTypes],
  );

  const sourceRelationTypesByFamily = useMemo(
    () =>
      allRelationTypes.reduce(
        (acc, type) => ({
          ...acc,
          [type]: sourceRelationTypes.filter(
            (relation) => relation.type === type,
          ),
        }),
        {} as Record<typeof allRelationTypes[number], typeof sourceRelationTypes>,
      ),
    [allRelationTypes, sourceRelationTypes],
  );

  const sourceRelationTypeIds = useMemo(
    () => {
      const ids = sourceRelationTypes.flatMap((relation) => {
        const selection = sourceRelationSelection[relation.type];

        if (selection.exclude) {
          return [];
        }

        if (selection.isCustom) {
          return selection.selectedTypeIds.includes(relation.id)
            ? [relation.id]
            : [];
        }

        return [relation.id];
      });

      if (!ids.length && excludeEmptySourceRelationType) {
        return [-1];
      }

      return ids;
    },
    [
      excludeEmptySourceRelationType,
      sourceRelationSelection,
      sourceRelationTypes,
    ],
  );

  const toggleSourceRelationFamilyExclude = (
    type: QuestionSourceRelationTypeEnum,
  ) => {
    resetTable();
    setSourceRelationSelection((prev) => ({
      ...prev,
      [type]: {
        exclude: !prev[type].exclude,
        isCustom: false,
        selectedTypeIds: [],
      },
    }));
  };

  const toggleSourceRelationType = (
    familyType: QuestionSourceRelationTypeEnum,
    relationTypeId: number,
  ) => {
    resetTable();
    setSourceRelationSelection((prev) => {
      const currentSelection = prev[familyType];
      const familyRelationTypeIds = sourceRelationTypesByFamily[familyType].map(
        ({ id }) => id,
      );
      const currentSelectedTypeIds = currentSelection.exclude
        ? []
        : currentSelection.isCustom
          ? currentSelection.selectedTypeIds
          : familyRelationTypeIds;
      const isSelected = currentSelectedTypeIds.includes(relationTypeId);
      const nextSelectedTypeIds = isSelected
        ? currentSelectedTypeIds.filter((id) => id !== relationTypeId)
        : [...currentSelectedTypeIds, relationTypeId];
      const isAllSelected =
        nextSelectedTypeIds.length === familyRelationTypeIds.length;
      const isNoneSelected = !nextSelectedTypeIds.length;

      return {
        ...prev,
        [familyType]: {
          exclude: isNoneSelected,
          isCustom: !isAllSelected && !isNoneSelected,
          selectedTypeIds:
            isAllSelected || isNoneSelected ? [] : nextSelectedTypeIds,
        },
      };
    });
  };

  const getRelationTypeShortName = (
    familyType: QuestionSourceRelationTypeEnum,
    relationTypeName: string,
  ) => relationTypeName.replace(`${familyType}_`, '');

  const getRelationFamilyLabel = (type: QuestionSourceRelationTypeEnum) => {
    const selection = sourceRelationSelection[type];

    if (selection.exclude) {
      return `Exclude ${type}`;
    }

    if (!selection.isCustom) {
      return type;
    }

    const selectedLabels = sourceRelationTypesByFamily[type]
      .filter((relation) => selection.selectedTypeIds.includes(relation.id))
      .map((relation) => getRelationTypeShortName(type, relation.name));

    return `${type}: ${selectedLabels.join(', ')}`;
  };

  const { data, loading, fetchMore } = useQuery(GET_QUESTIONS, {
    fetchPolicy: 'network-only',
    variables: {
      paging: {
        page: 1,
        limit: FETCH_LIMIT,
      },
      sort,
      filter: {
        ...filterInput,
        isNeedFamily: familyToggle
          ? Boolean(familyToggle && filterInput.originalId)
          : false,
        created: isContentToggleAvailable
          ? isShowTeacherContent
            ? undefined
            : EntityCreatedRole.Nalaprep
          : undefined,
        sourceRelationTypeIds: sourceRelationTypeIds,
        excludeEmptySourceRelationType,
      },
    },
  });

  const questionsFragment = useFragment(
    QUESTION_TABLE_FRAGMENT,
    data?.question.getQuestions.results,
  );

  const infinitySortFamilyTree = (
    father: QuestionTableFragment,
    storage: QuestionTableFragment[],
  ) => {
    (questionsFragment || []).forEach((q) => {
      if (q.sourceQuestion?.id === father.id) {
        storage.push(q);

        infinitySortFamilyTree(q, storage);
      }
    });
  };

  const sortedQuestions = useMemo(() => {
    const questions = questionsFragment || [];
    if (familyToggle) {
      const storage: QuestionTableFragment[] = [];
      const questionIds = new Set(questions.map((q) => q.id));

      const rootQuestions = questions.filter(
        (q) => !q.sourceQuestion?.id || !questionIds.has(q.sourceQuestion.id),
      );

      rootQuestions.forEach((rootQuestion) => {
        storage.push(rootQuestion);

        infinitySortFamilyTree(rootQuestion, storage);
      });

      const mapOfQuestionsBySourceId: Record<string, QuestionTableFragment[]> =
        {};

      storage.forEach((q) => {
        if (!q.sourceQuestion?.id) {
          mapOfQuestionsBySourceId[`${q.id}-source`] = [q];

          return;
        }

        const sourceIdKey = `${q.sourceQuestion.id}-children`;

        if (!mapOfQuestionsBySourceId[sourceIdKey]) {
          mapOfQuestionsBySourceId[sourceIdKey] = [q];

          return;
        }

        if (mapOfQuestionsBySourceId[sourceIdKey]) {
          mapOfQuestionsBySourceId[sourceIdKey] = [
            ...mapOfQuestionsBySourceId[sourceIdKey],
            q,
          ];

          return;
        }
      });

      const relationTypeOrder = [
        'Repeat_Identical',
        'Repeat_DiffNames',
        'Repeat_Variant',
        'Similar_SameQLogic',
        'Similar_DiffQLogic',
        'Related_Topic',
      ];

      const sortedValues = Object.values(mapOfQuestionsBySourceId).map(
        (questions) => {
          return questions.sort((a, b) => {
            // Питання без sourceRelationType йдуть в кінець
            if (!a.sourceRelationType && !b.sourceRelationType) return 0;
            if (!a.sourceRelationType) return 1;
            if (!b.sourceRelationType) return -1;

            const aType = a.sourceRelationType.name;
            const bType = b.sourceRelationType.name;

            const aIndex = relationTypeOrder.indexOf(aType);
            const bIndex = relationTypeOrder.indexOf(bType);

            const aOrder = aIndex === -1 ? Infinity : aIndex;
            const bOrder = bIndex === -1 ? Infinity : bIndex;

            return aOrder - bOrder;
          });
        },
      );

      return sortedValues.flat();
    }

    return questions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsFragment]);

  const questions = sortedQuestions || [];
  const totalQuestions = data?.question.getQuestions?.total ?? 0;
  const familyQuestionCountText = `${questions.length} ${
    questions.length === 1 ? 'question' : 'questions'
  } in family`;

  const { selected, onSelect, onSelectAmount, onResetAll } = useSelectItem(
    questions.map((question) => question.id),
    setSelectedQuestion,
  );

  const [remove] = useMutation(REMOVE_QUESTION, {
    refetchQueries: [GET_QUESTIONS],
  });

  const onRemoveQuestion = (ids: number[]) => {
    remove({
      awaitRefetchQueries: true,
      refetchQueries: [GET_QUESTIONS],
      variables: {
        input: {
          ids,
        },
      },
    });

    onResetAll();
    setPage(1);
    setIsDeleted(true);
  };

  useEffect(() => {
    resetTable();
  }, [resetTable, sort, isShowTeacherContent]);

  const onFetchMore = (setFetchMore: Dispatch<SetStateAction<boolean>>) => {
    const total = Number(data?.question.getQuestions.total);
    if (total && Number(sortedQuestions?.length) < total) {
      const nextPage = page + 1;
      setPage(nextPage);
      setFetchMore(true); // function fetchMore doesnt trigger loading status...
      return fetchMore({
        variables: {
          paging: {
            page: nextPage,
            limit: FETCH_LIMIT,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.question?.getQuestions?.results) return prev;

          const union = [
            ...(prev.question?.getQuestions?.results || []),
            ...fetchMoreResult.question.getQuestions.results,
          ];
          const uniqueQuestions = uniqBy(union, 'id');

          if (uniqueQuestions.length !== union.length)
            console.error('duplicates on merge detected'); // TODO remove after dev stage

          return {
            question: {
              ...prev.question,
              getQuestions: {
                ...fetchMoreResult.question.getQuestions,
                results: uniqueQuestions,
              },
            },
          };
        },
      });
    }
  };
  const { ref, isFetchMore, setFetchMore } = useFetchMore(
    onFetchMore,
    onDelete,
    tableContainerRef,
  );

  const getNext = async (id: number) => {
    const index = questions.findIndex((question) => question.id === id);
    const nextId = questions[index + 1]?.id;
    return {
      id:
        nextId ||
        (await onFetchMore(setFetchMore))?.data.question.getQuestions.results[0]
          ?.id,
      index: Number(index) + 1,
    };
  };

  const getPrev = (id: number) => {
    const index = questions.findIndex((question) => question.id === id);
    return {
      id: questions[index - 1]?.id,
      index: Number(index) - 1,
    };
  };

  const closePreview = () => setPreviewQuestion(null);

  const onPreviewNext = async () => {
    if (!previewQuestion || previewQuestion.index === totalQuestions - 1)
      return;

    const next = await getNext(previewQuestion.id);

    if (next.id) {
      setPreviewQuestion({ id: next.id, index: next.index });
    }
  };

  const onPreviewPrev = () => {
    if (!previewQuestion?.index) return;

    const prev = getPrev(previewQuestion.id);

    if (prev.id) {
      setPreviewQuestion({ id: prev.id, index: prev.index });
    }
  };

  const relationTypeComponent = (relationType: string) => {
    const params = questionDourceRelationTypeColorOrder[relationType];
    return (
      <div
        className="data-[highlighted]:bg-gray-100 my-2 flex max-w-44 cursor-pointer select-none items-center gap-2 rounded px-2 py-1 outline-none"
        style={{
          color: params.textColor,
          backgroundColor: params.bgColor,
        }}
      >
        <div>{relationType}</div>
      </div>
    );
  };

  return (
    <div>
      <section className={'flex justify-between'}>
        <div className={'grid grid-cols-1 gap-1'}>
          {filters.map((filter, index, array) => {
            const isLast = index === array.length - 1;
            return (
              <div className={'flex items-center'} key={filter.id}>
                <QuestionsFilter
                  filter={filter}
                  currentFilters={filters}
                  setFilters={setFilters}
                  onAddFilter={
                    isLast && filters.length < questionsBrowserFilters.length
                      ? onAddFilter
                      : undefined
                  }
                  onRemoveFilter={
                    array.length === 1 ? onClearFilter : onRemoveFilter
                  }
                  except={isQuestionsDrafts ? 'status' : undefined}
                />
                {isContentToggleAvailable && isLast ? <ContentToggle /> : null}
              </div>
            );
          })}
        </div>

        <div
          className={'flex items-center justify-center gap-2 whitespace-nowrap'}
        >
          {!isModalMode && (
            <ActionsDropdown
              selected={selected}
              browser={NavBarEnum.QUESTIONS_BROWSER}
              onRemove={onRemoveQuestion}
            />
          )}

          <div className={'text-sm text-gray'}>
            <p>
              {familyToggle
                ? familyQuestionCountText
                : `${totalQuestions} questions match your filters`}
            </p>
            <p>{selected.length} questions selected</p>
          </div>
        </div>
      </section>

      <div className="my-2 flex items-center gap-2">
        <div className="flex">
          <Button
            onClick={() => {
              if (!isFamilyAvailable) return;

              setFamilyToggle((prev) => !prev);
            }}
            className="h-7 rounded-md px-3 py-1 text-sm font-medium"
            disabled={!isFamilyAvailable}
            tip={
              !isFamilyAvailable
                ? 'Family view requires exactly one question ID.'
                : undefined
            }
            {...(familyToggle ? { blue: true } : { white: true })}
          >
            Family
          </Button>
        </div>

        <div className="flex gap-2">
          <div className="flex gap-2">
            {allRelationTypes.map((type) => (
              <Popover
                key={type}
                triggerComponent={
                  <button
                    className={cn(
                      'rounded-md bg-[#2b2b2b25] px-2 py-1 text-xs font-medium hover:bg-[#2b2b2b35]',
                      !sourceRelationSelection[type].exclude &&
                        !sourceRelationSelection[type].selectedTypeIds.length &&
                        'opacity-50',
                    )}
                    type="button"
                  >
                    {getRelationFamilyLabel(type)}
                  </button>
                }
                contentProps={{ align: 'start', className: 'w-72 p-3' }}
              >
                <div className="grid gap-3 text-sm">
                  <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-light-blue">
                    <input
                      type="checkbox"
                      checked={sourceRelationSelection[type].exclude}
                      onChange={() => toggleSourceRelationFamilyExclude(type)}
                    />
                    <span className="font-medium">Exclude all {type}</span>
                  </label>

                  <div className="grid gap-1">
                    <span className="px-2 text-xs font-medium text-gray">
                      Show
                    </span>

                    {sourceRelationTypesByFamily[type].map((relation) => (
                      <label
                        key={relation.id}
                        className={cn(
                          'flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-light-blue',
                          sourceRelationSelection[type].exclude && 'opacity-60',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={
                            !sourceRelationSelection[type].exclude &&
                            (sourceRelationSelection[type].isCustom
                              ? sourceRelationSelection[
                                  type
                                ].selectedTypeIds.includes(relation.id)
                              : true)
                          }
                          onChange={() =>
                            toggleSourceRelationType(type, relation.id)
                          }
                        />
                        <span>{relation.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </Popover>
            ))}

            <Popover
              triggerComponent={
                <button
                  className={cn(
                    'rounded-md bg-[#2b2b2b25] px-2 py-1 text-xs font-medium hover:bg-[#2b2b2b35]',
                    !excludeEmptySourceRelationType && 'opacity-50',
                  )}
                  type="button"
                >
                  {excludeEmptySourceRelationType ? 'Exclude Blank' : 'Blank'}
                </button>
              }
              contentProps={{ align: 'start', className: 'w-72 p-3' }}
            >
              <div className="grid gap-3 text-sm">
                <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-light-blue">
                  <input
                    type="checkbox"
                    checked={excludeEmptySourceRelationType}
                    onChange={() => {
                      resetTable();
                      setExcludeEmptySourceRelationType((prev) => !prev);
                    }}
                  />
                  <span className="font-medium">Exclude Blank Relations</span>
                </label>

                <div className="grid gap-1">
                  <span className="px-2 text-xs font-medium text-gray">
                    Show
                  </span>
                  <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-light-blue">
                    <input
                      type="checkbox"
                      checked={!excludeEmptySourceRelationType}
                      onChange={() => {
                        resetTable();
                        setExcludeEmptySourceRelationType(false);
                      }}
                    />
                    <span>Blank Relations</span>
                  </label>
                </div>
              </div>
            </Popover>
          </div>
        </div>
      </div>

      <QuestionsTable
        selected={selected}
        onSelectAmount={onSelectAmount}
        onResetAll={onResetAll}
        sort={sort}
        setSort={setQuestionSort}
        isLoading={loading || isFetchMore}
        isEmpty={!questions.length}
        containerRef={tableContainerRef}
      >
        {questions.map((question, index, array) => {
          const isChoiceType =
            question.answerType !== QuestionAnswerType.TextEntry;
          return (
            <tr
              key={question.id}
              ref={index === array.length - 3 ? ref : null}
              className="h-max"
            >
              <td className="flex text-center">
                <input
                  type={'checkbox'}
                  checked={selected.includes(question.id)}
                  onChange={() => onSelect(question.id)}
                />
              </td>

              <td>
                <QuestionIdTableComponent
                  id={question.id}
                  displayValue={question.originalId}
                  onPreview={() =>
                    setPreviewQuestion({ id: question.id, index })
                  }
                />
              </td>

              <td>{question.sourceQuestion?.originalId}</td>

              <td>
                {question.sourceRelationType &&
                  relationTypeComponent(question.sourceRelationType?.name)}
              </td>
              <td>
                <AnswerCell field={'answer'} question={question} />
              </td>
              <td>{capitalizeWords(question.usageType || '')}</td>
              <td>
                <Markdown>{question.content ?? ''}</Markdown>
              </td>
              <td>
                <Markdown>{question.prompt}</Markdown>
              </td>

              <td>
                <Difficulty level={question.difficulty.id} />
              </td>
              <td>{capitalizeWords(question.skill?.name)}</td>
              <td>{isChoiceType ? 'Multiple' : 'Text'}</td>
              <td>{capitalizeWords(question.domain?.name)}</td>
              <td>{capitalizeWords(question.subject?.name)}</td>
              <td>{capitalizeWords(question.type1?.name)}</td>

              <td>{capitalizeWords(question.type2?.name)}</td>
              <td>
                <div className={'flex flex-nowrap'}>
                  {question.tags?.map((tag, index) => (
                    <Tag title={capitalizeWords(tag.name)} key={index} />
                  ))}
                </div>
              </td>

              <td>
                <AnswerCell field={'a'} question={question} />
              </td>
              <td>
                <AnswerCell field={'b'} question={question} />
              </td>
              <td>
                <AnswerCell field={'c'} question={question} />
              </td>
              <td>
                <AnswerCell field={'d'} question={question} />
              </td>
              <td>{capitalizeWords(question.sourceType || '')}</td>

              <td>{question.explanation}</td>
              <td>{question.created}</td>
              <td>{question.sourceText}</td>
              <td>{question.sourceAuthor}</td>
            </tr>
          );
        })}
      </QuestionsTable>

      <Modal
        title={'Question preview'}
        isOpen={Boolean(previewQuestion)}
        onClose={closePreview}
        fullScreen
      >
        {previewQuestion && (
          <QuestionPreview
            questionId={previewQuestion.id}
            onNext={onPreviewNext}
            onPrev={onPreviewPrev}
            total={totalQuestions}
            currentIndex={previewQuestion.index}
          />
        )}
      </Modal>
    </div>
  );
};
export default QuestionsBrowser;
